import { useAppSelector, useAppStore } from 'store/hooks';
import { selectFeatureFlag } from 'reducers/selectors';
import { useCallback } from 'react';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useProRataAmount from 'features/promotions/hooks/useProRataAmount';
import { selectIsUserOnAnnual, selectIsUserOnPremium, selectUserTier, } from '../selectors/tier';
import { isPremiumTierPeriodUpgrade, tierGreaterThan, TierPeriod, } from '../entities';
import { selectPaymentFailed, selectShouldShowStripe } from '../selectors';
import useFailedPaymentModal from './useFailedPaymentModal';
import useNavigateToPartialRefund from './useNavigateToPartialRefund';
const usePremiumPlanChangeExperience = () => {
    const store = useAppStore();
    const navigation = useMainStackNavigation();
    const isStripeUser = useAppSelector(selectShouldShowStripe);
    const userIsOnPremium = useAppSelector(selectIsUserOnPremium);
    const userTier = useAppSelector(selectUserTier);
    const isUserTierAnnual = useAppSelector(selectIsUserOnAnnual);
    const isInstantProratedRefunds = useAppSelector((state) => selectFeatureFlag(state, 'instant_prorated_refunds').value);
    const showPlanChangeScreen = useCallback((params) => {
        navigation.navigateDeprecated('PremiumUpgradeScreen', params);
    }, []);
    const { isFetching: isFetchingPartialRefund, getDiscountAndPartialRefundPreview, navigateToDowngradeOffer, } = useNavigateToPartialRefund(userTier);
    const { getPreview, isFetchingStripePreview } = useProRataAmount();
    const { showFailedPaymentModal, isFetching: isFetchingPayment } = useFailedPaymentModal();
    const onInstantPartialRefundScreen = useCallback(async (subscription, callBack, discount, discountPrices) => {
        const preview = await getPreview(subscription.id, discount?.type === 'stripe' ? discount : undefined);
        const params = {
            userTier,
            subscription,
            onContinue: callBack,
            preview,
            isSamePlan: true,
            discountPrices,
        };
        showPlanChangeScreen(params);
    }, [getPreview, showPlanChangeScreen, userTier]);
    const isFetching = isFetchingStripePreview || isFetchingPartialRefund || isFetchingPayment;
    const handleNext = (callBack, onClose) => {
        if (onClose) {
            onClose(undefined, () => callBack());
        }
        else {
            callBack();
        }
    };
    const startFlow = async (newSubscription, onSubscribeCallback, onClose) => {
        if (selectPaymentFailed(store.getState())) {
            handleNext(() => {
                showFailedPaymentModal(() => {
                    startFlow(newSubscription, onSubscribeCallback);
                });
            }, onClose);
            return;
        }
        if (!isStripeUser) {
            handleNext(() => onSubscribeCallback(newSubscription), onClose);
        }
        else {
            const { tier: newTier, period: newPeriod } = newSubscription;
            const isPlanUpgrade = tierGreaterThan(newTier, userTier);
            const isSamePlanUpgrade = userIsOnPremium &&
                isPremiumTierPeriodUpgrade(newTier, newPeriod, userTier, isUserTierAnnual ? TierPeriod.yearly : TierPeriod.monthly);
            const isUpgrade = isPlanUpgrade || isSamePlanUpgrade;
            if (userIsOnPremium &&
                newPeriod !==
                    (isUserTierAnnual ? TierPeriod.yearly : TierPeriod.monthly)) {
                const planChangeParams = {
                    userTier,
                    subscription: newSubscription,
                    onContinue: () => onSubscribeCallback(newSubscription),
                    isUpgrade,
                };
                planChangeParams.preview = await getPreview(newSubscription.id);
                if (isUserTierAnnual && !isUpgrade && isInstantProratedRefunds) {
                    const discountDataAndPreview = await getDiscountAndPartialRefundPreview();
                    handleNext(() => navigateToDowngradeOffer(() => showPlanChangeScreen(planChangeParams), 'downgrade', discountDataAndPreview), onClose);
                }
                else {
                    handleNext(() => showPlanChangeScreen(planChangeParams), onClose);
                }
            }
            else {
                handleNext(() => onSubscribeCallback(newSubscription), onClose);
            }
        }
    };
    return {
        startFlow,
        isFetching,
        onInstantPartialRefundScreen,
    };
};
export default usePremiumPlanChangeExperience;
